import { ScrollableArea } from "@nova/component-scrollable-area"
import { zIndex } from "@nova/constants"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    closeFullPanel,
    selectActiveSearchPanelPlugin,
    selectIsFullPanelOpen,
    selectRecentSearchesPanelOpen,
    selectSearchQuery,
} from "../slice"
import { ActivatablePluginName, filterRecentSearches, getActivatablePlugin } from "./registry"
import { selectRecentSearchResults } from "./selectors"
import { SearchResultRow } from "./SearchResultRow"
import { recordRecentSearch } from "./thunks"
import { useScopedSearchResults } from "./useSearchResults"
import { useEffect } from "react"
import { groupByDateBucket } from "tools/dates/dateBuckets"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"

export const OverviewSearchPanel = () => {
    const dispatch = useAppDispatch()
    const open = useAppSelector(selectIsFullPanelOpen)
    const activePlugin = useAppSelector(selectActiveSearchPanelPlugin)
    const recentSearchesPanelOpen = useAppSelector(selectRecentSearchesPanelOpen)

    useEffect(() => {
        if (open && !activePlugin && !recentSearchesPanelOpen) {
            dispatch(closeFullPanel())
        }
    }, [activePlugin, dispatch, open, recentSearchesPanelOpen])

    const ActivePluginHeaderComponent = activePlugin
        ? getActivatablePlugin(activePlugin).FullPanelHeaderComponent
        : null

    return (
        <div
            className={classNames(
                "absolute left-0 top-0 bottom-0 w-[384px] bg-black-800 backdrop-blur-sm pt-[72px] transition-transform pointer-events-auto flex flex-col gap-4 duration-300 ease-out",
                {
                    "-translate-x-full": !open,
                    "translate-x-0": open,
                }
            )}
            style={{ zIndex: zIndex.SidePanel }}
        >
            {recentSearchesPanelOpen && (
                <div className="pt-2 px-4 text-white text-lg telegraf">Recently Searched</div>
            )}
            {ActivePluginHeaderComponent && <ActivePluginHeaderComponent />}
            <div className="w-full h-px bg-white-200" />
            <ScrollableArea className="h-full" onWheel={(e) => e.stopPropagation()}>
                {activePlugin ? (
                    <ScopedResults pluginName={activePlugin} />
                ) : (
                    recentSearchesPanelOpen && <RecentSearchesBody />
                )}
            </ScrollableArea>
        </div>
    )
}

const ScopedResults = ({ pluginName }: { pluginName: ActivatablePluginName }) => {
    const dispatch = useAppDispatch()
    const query = useAppSelector(selectSearchQuery)
    const { results, isLoading } = useScopedSearchResults(pluginName, query)
    const EmptyComponent = getActivatablePlugin(pluginName).FullPanelEmptyComponent

    if (isLoading) return <div>Loading...</div>

    if (!results.length) {
        return EmptyComponent ? (
            <EmptyComponent query={query} />
        ) : (
            <div className="px-3 py-2 text-gray-500">No results</div>
        )
    }

    return (
        <div className="flex flex-col gap-0.5">
            {results.map((entry) => (
                <SearchResultRow
                    key={entry.plugin.getKey(entry.data)}
                    entry={entry}
                    variant="full"
                    onAfterSelect={() => {
                        dispatch(
                            recordRecentSearch({ pluginName: entry.plugin.name, data: entry.data })
                        )
                        dispatch(closeFullPanel())
                    }}
                />
            ))}
        </div>
    )
}

const RecentSearchesBody = () => {
    const dispatch = useAppDispatch()
    const results = useAppSelector(selectRecentSearchResults)
    const query = useAppSelector(selectSearchQuery)
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const filteredResults = filterRecentSearches(results, query, { orgId })

    if (!filteredResults.length) {
        return (
            <div className="px-3 py-2 text-gray-500">
                {query ? "No matching recent searches" : "No recent searches yet"}
            </div>
        )
    }

    const buckets = groupByDateBucket(filteredResults, (entry) => new Date(entry.timestamp))

    return (
        <div className="flex flex-col gap-4">
            {buckets.map(({ bucket, items }) => (
                <div key={bucket.key} className="flex flex-col">
                    <small className="py-2 px-4 xs text-gray-400">{bucket.label}</small>
                    {items.map((entry) => (
                        <SearchResultRow
                            key={entry.plugin.getKey(entry.data)}
                            entry={entry}
                            variant="full"
                            onAfterSelect={() => {
                                dispatch(
                                    recordRecentSearch({
                                        pluginName: entry.plugin.name,
                                        data: entry.data,
                                    })
                                )
                                dispatch(closeFullPanel())
                            }}
                        />
                    ))}
                </div>
            ))}
        </div>
    )
}
